import {
  isDataExtractionCustomField,
  type CustomField,
} from "@repo/app-types/entities/customField";
import type {
  AccountGradingContact,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Label } from "@repo/ui/Label/label";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconExternalLink, IconSparkles } from "@tabler/icons-react";
import { useMemo, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import {
  usePlaybookGradingCustomFieldsQuery,
  usePlaybookGradingOtherGradingsQuery,
} from "../../api/entities/performancePlaybook.js";
import { useAllMinimalPlaybooksQuery } from "../../api/entities/playbook.js";
import { appPaths } from "../../app/appPaths.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { CustomFieldValue } from "../interviews/interviewSlideover/customFieldValue.js";
import { renderTeammateName } from "./teammateHelpers.js";

export interface GradingCustomerSidebarProps {
  gradingId: GradingId;
  contact: AccountGradingContact;
}

interface FieldDisplayProps {
  label: ReactNode;
  value: ReactNode;
}

export const GradingCustomerSidebar: FC<GradingCustomerSidebarProps> = ({
  gradingId,
  contact,
}) => {
  const { data: gradingCustomFields } = usePlaybookGradingCustomFieldsQuery({
    gradingId,
  });

  const { data: otherGradingsForAccountData } =
    usePlaybookGradingOtherGradingsQuery({
      gradingId,
      limit: 5,
    });

  const { data: minimalPlaybooks } = useAllMinimalPlaybooksQuery();

  const otherGradingsTeammateNameById = useMemo(() => {
    if (!otherGradingsForAccountData?.basicTeammates) {
      return new Map<string, string | null>();
    }
    return new Map(
      otherGradingsForAccountData.basicTeammates.map((teammate) => [
        teammate.id,
        renderTeammateName(teammate) as string | null,
      ]),
    );
  }, [otherGradingsForAccountData?.basicTeammates]);

  const otherGradingsPlaybookNameById = useMemo(() => {
    if (!minimalPlaybooks) {
      return new Map<string, string>();
    }
    return new Map(
      minimalPlaybooks.map((playbook) => [playbook.id, playbook.name]),
    );
  }, [minimalPlaybooks]);

  const renderContactSection = () => {
    const contactFields: CustomField[] = [];

    // Contact custom fields (non-data extraction only). Grading custom fields
    // render in their own "Customer interaction" section below.
    if (contact.custom_fields?.length) {
      contact.custom_fields.forEach((field) => {
        if (!isDataExtractionCustomField(field)) {
          contactFields.push(field);
        }
      });
    }

    const hasContactInfo =
      contact.email || contact.phone_number || contact.ext_id;
    const hasContactFields = contactFields.length > 0;

    if (!hasContactInfo && !hasContactFields) {
      return null;
    }

    return (
      <div className="space-y-3">
        <SectionHeader title="Contact" />
        {contact.email && <FieldDisplay label="Email" value={contact.email} />}

        {contact.phone_number && (
          <FieldDisplay label="Phone" value={contact.phone_number} />
        )}

        {contact.ext_id && (
          <FieldDisplay label="External ID" value={contact.ext_id} />
        )}

        {contactFields.map((field) => (
          <FieldDisplay
            key={field.field_name}
            label={field.display_name}
            value={<CustomFieldValue field={field} />}
          />
        ))}
      </div>
    );
  };

  const renderCustomerInteractionMetadataSection = () => {
    // Manually-set customer interaction metadata: user-provider grading fields
    // (everything on the grading that isn't an AI data extraction field).
    const metadataFields = (gradingCustomFields ?? []).filter(
      (field) => !isDataExtractionCustomField(field),
    );

    if (metadataFields.length === 0) {
      return null;
    }

    return (
      <div className="space-y-3">
        <SectionHeader title="Customer interaction" />
        {metadataFields.map((field) => (
          <FieldDisplay
            key={field.field_name}
            label={field.display_name}
            value={<CustomFieldValue field={field} />}
          />
        ))}
      </div>
    );
  };

  const renderDataExtractionFields = () => {
    const dataExtractionFields: {
      field: CustomField;
      isGradingField: boolean;
    }[] = [];

    // Collect data extraction fields from contact custom fields
    if (contact.custom_fields?.length) {
      contact.custom_fields.forEach((field) => {
        if (isDataExtractionCustomField(field)) {
          dataExtractionFields.push({ field, isGradingField: false });
        }
      });
    }

    // Collect data extraction fields from grading custom fields
    if (gradingCustomFields?.length) {
      gradingCustomFields.forEach((field) => {
        if (isDataExtractionCustomField(field)) {
          dataExtractionFields.push({ field, isGradingField: true });
        }
      });
    }

    if (dataExtractionFields.length === 0) {
      return null;
    }

    return (
      <div className="space-y-3">
        <SectionHeader
          icon={<IconSparkles className="size-4 text-purple-600" />}
          title="Data extraction"
        />
        {dataExtractionFields.map(({ field, isGradingField }) => (
          <FieldDisplay
            key={field.field_name}
            label={field.display_name}
            value={
              <CustomFieldValue
                field={field}
                disregardDisplayValue={isGradingField}
              />
            }
          />
        ))}
      </div>
    );
  };

  const renderOtherCustomerInteractions = () => {
    if (
      !otherGradingsForAccountData ||
      otherGradingsForAccountData.otherGradings.length === 0
    ) {
      return null;
    }

    const otherInteractions = otherGradingsForAccountData.otherGradings.map(
      (grading) => {
        const playbookName = otherGradingsPlaybookNameById.get(
          grading.playbook_id,
        );
        const teammateName = otherGradingsTeammateNameById.get(
          grading.teammate_id,
        );

        return {
          id: grading.id,
          playbookId: grading.playbook_id,
          teammateName,
          playbookName,
          date: grading.last_recorded_at,
        };
      },
    );

    return (
      <div className="space-y-3">
        <SectionHeader title="Other interactions" />
        <div className="space-y-2">
          {otherInteractions.map((interaction) => (
            <a
              key={interaction.id}
              href={appPaths.playbookCall.build({
                playbookId: interaction.playbookId,
                gradingId: interaction.id,
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block rounded-md border border-border-light bg-white px-3 py-2 transition-colors hover:border-border-strong hover:bg-gray-50"
            >
              <div className="min-w-0 space-y-1">
                {interaction.teammateName ? (
                  <TooltipTrigger>
                    <TooltipHoverTarget className="block truncate text-xs font-medium text-foreground">
                      {interaction.teammateName}
                    </TooltipHoverTarget>
                    <SimpleTooltip>{interaction.teammateName}</SimpleTooltip>
                  </TooltipTrigger>
                ) : null}
                {interaction.playbookName ? (
                  <TooltipTrigger>
                    <TooltipHoverTarget className="block truncate text-xs text-gray-600">
                      {interaction.playbookName}
                    </TooltipHoverTarget>
                    <SimpleTooltip>{interaction.playbookName}</SimpleTooltip>
                  </TooltipTrigger>
                ) : null}
                <div className="text-xs text-muted-foreground">
                  {formatDate(interaction.date)}
                </div>
              </div>
              <IconExternalLink className="absolute top-2 right-2 h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full w-[260px] flex-col border-l border-border-light bg-background">
      <div className="relative flex-1 overflow-y-auto p-4">
        <div className="space-y-5">
          {renderOtherCustomerInteractions()}
          {renderContactSection()}
          {renderCustomerInteractionMetadataSection()}
          {renderDataExtractionFields()}
        </div>
      </div>
    </div>
  );
};

const SectionHeader: FC<{ icon?: ReactNode; title: string }> = ({
  icon,
  title,
}) => (
  <div className="flex items-center gap-1.5 border-b border-border-light pb-2">
    {icon}
    <Label size={Size.SMALL}>{title}</Label>
  </div>
);

const FieldDisplay: FC<FieldDisplayProps> = ({ label, value }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
      {label}
    </div>
    {/* Labels are company configuration; only the values are contact PII. */}
    <div
      className={twMerge("text-sm break-words text-foreground", PH_MASK_CLASS)}
    >
      {value}
    </div>
  </div>
);

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});
const timeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return `${dateFormatter.format(date)} at ${timeFormatter.format(date)}`;
}
